import { useFormik } from "formik";
import * as yup from 'yup';
import { useCallback, useEffect, useMemo } from "react";
import { initialValues } from "../constants/constants";
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from "react-redux";
import { getData, getErrorMessage, getStatus } from "@/store/slices/helpers/helpers";
import { clearData, fetchData } from "@/store/slices/fetch-slice";
import { apiKeys, apiMethods, apiStatuses, apiUrls } from "@/store/api/constants";
import { useNavigate } from "react-router";
import { getDecoded, getFormattedPayload, getFormattedResponse } from "@/common/helpers/helpers";
import { useParams } from "react-router-dom";
import navs from "@/from-theme/routes/constants/nav";
import { toast } from 'react-toastify';

function useCreateEditSchemeStatusMaster({ isEditMode, getPlant, getCityRawData }) {
  const { id: competitorIdEncoded } = useParams();
  const competitorId = getDecoded(competitorIdEncoded);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const state = useSelector((store) => store);


  const updateSchemesListMasterStatus = getStatus(state, apiKeys.updateSchemesListMaster, '');
  const rejectSchemesListMasterStatus = getStatus(state, apiKeys.rejectSchemesListMaster, '');
  // const saveCompetitorMasterLoading = apiStatuses.pending === saveCompetitorMasterStatus;
  const getEditSchemesListMasterData = getData(state, apiKeys.getEditSchemesListMaster, {});
  const getEditSchemesListMasterStatus = getStatus(state, apiKeys.getEditSchemesListMaster, "");
  const getEditSchemesListMasterErrMsg = getErrorMessage(
    state,
    apiKeys.getEditSchemesListMaster,
    formatMessage({ id: "failed-to-get-selected-scheme-details" })
  );

  const validationSchema = yup.object({
    // activeStatus: yup.boolean().nullable(),
  });

  /**
   * Helper to parse JSON strings safely
   */
  const parseJsonField = (field) => {
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch (e) {
        return {};
      }
    }
    return field || {};
  };

  /**
   * Eligibility check for Maida individual categories
   */
  const checkMaidaEligibility = (monthSales, targetMts) => {
    const categories = ["PA", "PB", "BA", "BB", "MUL"];

    // Parse both sales and target as objects
    const salesObj = parseJsonField(monthSales);
    const targetObj = parseJsonField(targetMts);

    // Return true if ANY category's sales >= its target (and target > 0)
    return categories.some((cat) => {
      const s = Number(salesObj[cat] || 0);
      const t = Number(targetObj[cat] || 0);
      return t > 0 && s >= t;
    });
  };

  /**
   * Check if a row is eligible based on scheme type
   */
  const isRowEligible = (row, schemeName) => {
    const isMaida = String(schemeName || "").toLowerCase().includes("maida");
    const isFeed = String(schemeName || "").toLowerCase().includes("feed");

    if (isFeed) {
      const fromSlab = Number(row.achievedSlabFrom || 0);
      const toSlab = Number(row.achievedSlabTo || 0);
      return fromSlab !== 0 || toSlab !== 0;
    } else if (isMaida) {
      return checkMaidaEligibility(row.monthSales, row.targetMts);
    } else {
      const sales = Number(row.monthSales || 0);
      const target = Number(row.targetMts || 0);
      const min = Number(row.minMts || 0);

      if (target > 0) {
        return sales >= target;
      } else if (min > 0) {
        return sales >= min;
      }
      return false;
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,

    onSubmit: (values) => {
      // ===============================
      // CREATE MODE (UNCHANGED)
      // ===============================
      if (!isEditMode) {
        saveCompetitorMaster(values);
        return;
      }

      const isFeed = String(values.schemeName || "")
        .toLowerCase()
        .includes("feed");

      const selectedRowsData = [];

      // ===============================
      // ITERATE CHILD DATA
      // ===============================
      values.childData.forEach((row) => {
        /* ---------------------------------
          1. ELIGIBILITY CALCULATION
        ---------------------------------- */
        const isEligible = isRowEligible(row, values.schemeName);

        /* ---------------------------------
          2. SAVE ONLY SELECTED ROWS
        ---------------------------------- */
        if (Number(row.selected) === 1) {
          selectedRowsData.push({
            ...row,
            // ✅ Explicitly map the new fields (with safe fallbacks)
            new_scheme_rs_per_ton: row.new_scheme_rs_per_ton || null,
            new_scheme_rs_per_kg: row.new_scheme_rs_per_kg || null,
            new_scheme_amount: row.new_scheme_amount || null,
            newAchievedSlabFrom: row.newAchievedSlabFrom || null,
            newAchievedSlabTo: row.newAchievedSlabTo || null,
            // Enforce backend-safe values
            selected: 1,
            eligible: isEligible ? 1 : 0,

            // Status:
            // 1 → Auto Eligible
            // 3 → Manual Override (User forced)
            // 4 -> Unplanned 
            status: row.id === null ? 4 : (isEligible ? 1 : 3),

            avgRuleOverAllGroupId: !isFeed
              ? values.avgRuleOverAllGroupId || ""
              : ""
          });
        }
      });

      // ===============================
      // VALIDATIONS
      // ===============================
      if (selectedRowsData.length === 0) {
        toast.error("No customers selected!");
        return;
      }

      // ===============================
      // FINAL PAYLOAD
      // ===============================
      console.log(selectedRowsData, 'selectedRowsData');
      updateSchemesListMaster(selectedRowsData);
    }

  });

  const { values } = formik;
  const { divisionId } = values;

  useEffect(() => {
    getCityRawData()
  }, [getCityRawData]);

  useEffect(() => {
    let payload;
    if (divisionId) {
      // set data 
      payload = getFormattedPayload({ divisionId });
      getPlant(payload);
      // clear data 
      formik.setFieldValue('userPlantId', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionId, getPlant]);


  formik.fields = validationSchema?.fields;
  formik.schema = validationSchema;
  const getEditSchemesListMaster = useCallback(() => {
    dispatch(
      fetchData({
        url: apiUrls.getEditSchemesListMaster(competitorId),
        method: apiMethods.GET,
        key: apiKeys.getEditSchemesListMaster,
        failureMsg: formatMessage({ id: "failed-to-get-selected-scheme-details" }),
      })
    );
  }, [dispatch, formatMessage, competitorId]);

  const goBack = useCallback(() => {
    navigate(navs.schemeStatusListView);
  }, [navigate]);


  const rejectProcess = useCallback((child_data) => {
    const selectedRowsData = child_data.filter(row => Number(row.selected) === 1)
    if (selectedRowsData.length === 0) {
      toast.error("Rejection process requires at least one selected customer");
      return false
    }
    // ❌ BLOCK ELIGIBLE ROWS FROM REJECTION
    const blocked = selectedRowsData.filter(row => isRowEligible(row, values.schemeName))
    // if (blocked.length > 0) {
    //   // alert("Eligible customers cannot be rejected")
    //   toast.error("Eligible customers cannot be rejected");
    //   return false
    // }
    // ✅ Allowed → Proceed reject
    const finalData = selectedRowsData.map(row => ({
      ...row,
      status: row.id === null ? 4 : 2
    }));
    rejectSchemesListMaster(finalData)
    return true
  }, [values.schemeName])


  useEffect(() => {
    if (isEditMode) {
      getEditSchemesListMaster()
    }
  }, [isEditMode, getEditSchemesListMaster]);

  useEffect(() => {
    if (getEditSchemesListMasterStatus === apiStatuses.succeeded) {
      formik.setValues(getFormattedResponse(getEditSchemesListMasterData));
    }
  }, [getEditSchemesListMasterData]);


  const saveCompetitorMaster = useCallback((values) => {
    dispatch(
      fetchData({
        url: apiUrls.saveCompetitorMaster,
        method: apiMethods.POST,
        key: apiKeys.saveCompetitorMaster,
        successMsg: formatMessage({ id: "scheme-details-is-saved-successfully" }),
        failureMsg: formatMessage({ id: "scheme-details-is-failed-to-save" }),
        body: getFormattedPayload(values)
      })
    );
  }, [dispatch, formatMessage]);

  const updateSchemesListMaster = useCallback((values) => {
    dispatch(
      fetchData({
        url: apiUrls.updateSchemesListMaster,
        method: apiMethods.POST,
        key: apiKeys.updateSchemesListMaster,
        successMsg: formatMessage({ id: "scheme-list-master-is-updated-successfully" }),
        failureMsg: formatMessage({ id: "scheme-list-master-is-failed-to-update" }),
        // body: getFormattedPayload({ ...values, id: isEditMode })
        body: getFormattedPayload({ ...values })
        // body: getFormattedPayload(values)
      })
    );
  }, [dispatch, formatMessage, isEditMode]);

  const rejectSchemesListMaster = useCallback((values) => {
    dispatch(
      fetchData({
        url: apiUrls.rejectSchemesListMaster,
        method: apiMethods.POST,
        key: apiKeys.rejectSchemesListMaster,
        successMsg: formatMessage({ id: "scheme-list-master-is-rejected-successfully" }),
        failureMsg: formatMessage({ id: "scheme-list-master-is-failed-to-reject" }),
        // body: getFormattedPayload({ ...values, id: isEditMode })
        body: getFormattedPayload({ ...values })
        // body: getFormattedPayload(values)
      })
    );
  }, [dispatch, formatMessage, isEditMode]);

  // useEffect(() => {
  //   if (saveCompetitorMasterStatus === apiStatuses.succeeded) {
  //     navigate(navs.ifdCompetitorMaster)
  //   }
  // }, [saveCompetitorMasterStatus, navigate])


  useEffect(() => {
    if (updateSchemesListMasterStatus === apiStatuses.succeeded) {
      navigate(navs.schemeStatusListView)
    }
  }, [updateSchemesListMasterStatus, navigate])

  useEffect(() => {
    if (rejectSchemesListMasterStatus === apiStatuses.succeeded) {
      navigate(navs.schemeStatusListView)
    }
  }, [rejectSchemesListMasterStatus, navigate])

  useEffect(() => () => {
    dispatch(clearData({ key: apiKeys.getAreaMaster }));
    dispatch(clearData({ key: apiKeys.getDropdownListMaster }));
    dispatch(clearData({ key: apiKeys.getCityMaster }));
    // dispatch(clearData({ key: apiKeys.saveCompetitorMaster })); 
    dispatch(clearData({ key: apiKeys.updateSchemesListMaster }));
    dispatch(clearData({ key: apiKeys.getEditSchemesListMaster }));
  }, [dispatch])

  // ==========================================================
  // ✅ FIX: Calculate Selection for ALL Rows on Data Load
  // ==========================================================
  useEffect(() => {
    if (getEditSchemesListMasterStatus === apiStatuses.succeeded) {
      // 1. Get raw response
      const responseData = getFormattedResponse(getEditSchemesListMasterData);

      // 2. Pre-process ALL childData immediately
      if (responseData?.childData && Array.isArray(responseData.childData)) {
        responseData.childData = responseData.childData.map((row) => {

          // Calculate Eligibility using your existing helper
          const eligible = isRowEligible(row, responseData.schemeName);

          return {
            ...row,
            selected: 0,        // Do not auto-check on load
            eligible: eligible ? 1 : 0,  // Backend flag
            autoSelected: 0 // Tracking flag
          };
        });
      }

      // 3. Set values into Formik
      formik.setValues(responseData);
    }
  }, [getEditSchemesListMasterData]);

  const updateSchemesListMasterLoading = apiStatuses.pending === updateSchemesListMasterStatus;
  const rejectSchemesListMasterLoading = apiStatuses.pending === rejectSchemesListMasterStatus;

  return useMemo(() => ({
    formik,
    values: formik.values,
    updateSchemesListMasterStatus,
    rejectSchemesListMasterStatus,
    updateSchemesListMasterLoading,
    rejectSchemesListMasterLoading,
    // saveCompetitorMasterLoading,
    getEditSchemesListMasterData,
    getEditSchemesListMasterStatus,
    getEditSchemesListMasterErrMsg,
    goBack,
    rejectProcess,
    competitorId
  }), [
    formik,
    updateSchemesListMasterStatus,
    rejectSchemesListMasterStatus,
    updateSchemesListMasterLoading,
    rejectSchemesListMasterLoading,
    // saveCompetitorMasterLoading,
    getEditSchemesListMasterData,
    getEditSchemesListMasterStatus,
    getEditSchemesListMasterErrMsg,
    goBack,
    rejectProcess,
    competitorId
  ])
}

export default useCreateEditSchemeStatusMaster